<template>
    <cic-popup
        :title="item.announcementId ? 'Edit Announcement' : 'New Announcement'"
        :editable="item.announcementId > 0"
        :saveable="editing"
        @blur="$emit('blur')"
        @edit="toggleEdit"
        @input="$emit('input')"
    >
        <div class="item-form">
            <ul>
                <li class="form-label-first">
                    {{ $t('Name') }}
                </li>
                <li>
                    <cic-textbox
                        id="announcement_name"
                        :value="item.name"
                        :enabled="editing"
                        :maxlength="255"
                        @input="(val) => updateFieldValue('name', val)"
                    />
                </li>
                <li class="form-label">
                    {{ $t('Message') }}
                </li>
                <li>
                    <cic-textarea
                        id="announcement_message"
                        :value="item.message"
                        :enabled="editing"
                        :autosize="true"
                        @input="(val) => updateFieldValue('message', val)"
                    />
                </li>
                <li class="form-label">
                    {{ $t('Rights') }}
                </li>
                <li>
                    <cic-select
                        id="announcement_rights"
                        :options="
                            $store.user.rights
                                .filter(
                                    (x) =>
                                        x.indexOf('_') === -1 &&
                                        x.substring(0, 1) !== 'b' &&
                                        x.substring(0, 2) !== 'sz',
                                )
                                .map((x) => ({ value: x, label: x }))
                        "
                        :value="item.rights"
                        :enabled="editing"
                        filterable
                        multiple
                        @input="(val) => updateFieldValue('rights', val)"
                    />
                </li>
                <li class="form-label">
                    {{ $t('Organisations') }}
                </li>
                <li>
                    <table id="organisations_table" class="cic-table">
                        <thead>
                            <tr>
                                <th>{{ $t('Name') }}</th>
                                <th>{{ $t('Apply To Suborgs') }}</th>
                                <th v-if="editing"></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="org in item.organisations">
                                <td>{{ organisations.find((x) => x.orgId === org.orgId).name }}</td>
                                <td>
                                    <cic-checkbox
                                        :value="org.applyToSubOrgs"
                                        :enabled="editing"
                                        @input="updateApplyToSubOrgs(org.orgId)"
                                    />
                                </td>
                                <td v-if="editing">
                                    <icon-button icon="remove-icon" @click="removeOrg(org.orgId)" />
                                </td>
                            </tr>
                            <tr v-if="editing">
                                <td>
                                    <cic-select
                                        :value="newOrg.orgId"
                                        :options="orgOptions"
                                        @input="(val) => (newOrg.orgId = val)"
                                    />
                                </td>
                                <td>
                                    <cic-checkbox
                                        :value="newOrg.applyToSubOrgs"
                                        @input="(val) => (newOrg.applyToSubOrgs = val)"
                                    />
                                </td>
                                <td><icon-button icon="add-icon" @click="addOrg" /></td>
                            </tr>
                        </tbody>
                    </table>
                </li>
                <template v-if="item.organisations.length > 0">
                    <li class="form-label">
                        {{ $t('Users') }}
                    </li>
                    <li>
                        <cic-select
                            id="announcement_users"
                            :multiple="true"
                            :options="userOptions"
                            :value="item.users"
                            :enabled="editing"
                            @input="(val) => updateFieldValue('users', val)"
                        />
                    </li>
                </template>
                <li class="form-label">
                    {{ $t('Start Time') }}
                </li>
                <li>
                    <cic-date-picker
                        id="announcement_start"
                        :value="item.start"
                        :enabled="editing"
                        type="datetime"
                        @input="(val) => updateFieldValue('start', val)"
                    />
                </li>
                <li class="form-label">
                    {{ $t('End Time') }}
                </li>
                <li>
                    <cic-date-picker
                        id="announcement_end"
                        :value="item.end"
                        :enabled="editing"
                        type="datetime"
                        @input="(val) => updateFieldValue('end', val)"
                    />
                </li>
            </ul>
        </div>
    </cic-popup>
</template>

<script>
    import CicCheckbox from '@/components/inputs/CicCheckbox';
    import CicDatePicker from '@/components/CicDatePicker';
    import CicPopup from '@/components/CicPopup';
    import IconButton from '@/components/controls/IconButton';
    import CicTextarea from '@/components/inputs/CicTextarea';
    import CicSelect from '@/components/inputs/CicSelect';
    import CicTextbox from '@/components/inputs/CicTextbox';

    export default {
        name: 'EditAnnouncement',
        components: {
            IconButton,
            CicCheckbox,
            CicDatePicker,
            CicPopup,
            CicSelect,
            CicTextarea,
            CicTextbox,
        },
        props: {
            item: {
                type: Object,
                required: true,
            },
            organisations: {
                type: Array,
                required: true,
            },
            users: {
                type: Array,
                required: true,
            },
        },
        emits: ['blur', 'input', 'updateFieldValue'],
        data() {
            return {
                editing: false,
                newOrg: { orgId: null, applyToSubOrgs: false },
                showUsers: true,
            };
        },
        computed: {
            userOptions() {
                return this.users.map((x) => {
                    let name = x.firstName && x.lastName ? `${x.firstName} ${x.lastName}` : x.username;
                    let orgName = this.organisations.find((y) => x.orgId === y.orgId).name;

                    return { value: x.userId, label: `${name} (${orgName})` };
                });
            },

            orgOptions() {
                return this.organisations
                    .filter((x) => !this.item.organisations.map((z) => z.orgId).includes(x.orgId))
                    .map((y) => ({ value: y.orgId, label: y.name }));
            },
        },
        created() {
            this.editing = this.item.announcementId <= 0;
        },
        methods: {
            addOrg() {
                if (this.newOrg.orgId) {
                    let orgs = JSON.parse(JSON.stringify(this.item.organisations));
                    orgs.push(this.newOrg);
                    this.updateFieldValue('organisations', orgs);
                    this.newOrg = { orgId: null, applyToSubOrgs: false };
                }
            },

            removeOrg(orgId) {
                this.updateFieldValue(
                    'organisations',
                    this.item.organisations.filter((x) => x.orgId !== orgId),
                );
            },

            toggleEdit() {
                this.editing = !this.editing;
            },

            updateApplyToSubOrgs(orgId) {
                let orgs = JSON.parse(JSON.stringify(this.item.organisations));
                let org = orgs.find((x) => x.orgId === orgId);
                org.applyToSubOrgs = !org.applyToSubOrgs;
                this.updateFieldValue('organisations', orgs);
            },

            updateFieldValue(field, value) {
                this.$emit('updateFieldValue', field, value);
            },
        },
    };
</script>
